import { useState, useRef, useEffect, useCallback, type ChangeEvent, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';

export interface FormulaTextAreaProps {
  label?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  error?: string;
  rows?: number;
  placeholder?: string;
  required?: boolean;
  variables: string[];
  /** Optional: map of variable name -> formula text. When a variable with a saved formula is selected, the formula is inserted into the field instead of just the name. */
  savedFormulas?: Record<string, string>;
}

export default function FormulaTextArea({
  label,
  value,
  onChange,
  error,
  rows = 3,
  placeholder,
  required,
  variables,
  savedFormulas = {},
}: FormulaTextAreaProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const textareaId = label ? `textarea-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined;
  const errorId = error ? `${textareaId}-error` : undefined;

  // Position dropdown relative to viewport (for portal rendering)
  const updateDropdownPosition = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;

    const taRect = ta.getBoundingClientRect();
    const style = window.getComputedStyle(ta);
    const lineHeight = parseInt(style.lineHeight) || parseInt(style.fontSize) * 1.4;

    // Place dropdown just below the textarea's top + one line
    // Simple approach: position below the textarea caret area
    if (mentionStart !== null) {
      // Create a mirror to find caret position
      const mirror = document.createElement('div');
      const props = [
        'fontFamily', 'fontSize', 'fontWeight', 'lineHeight', 'letterSpacing',
        'paddingTop', 'paddingLeft', 'paddingRight', 'borderTopWidth', 'borderLeftWidth',
        'boxSizing', 'overflowWrap',
      ] as const;
      mirror.style.position = 'absolute';
      mirror.style.visibility = 'hidden';
      mirror.style.width = style.width;
      mirror.style.whiteSpace = 'pre-wrap';
      mirror.style.wordWrap = 'break-word';
      props.forEach((p) => {
        (mirror.style as any)[p] = style.getPropertyValue(
          p.replace(/([A-Z])/g, '-$1').toLowerCase()
        );
      });

      const textBefore = value.substring(0, mentionStart);
      mirror.appendChild(document.createTextNode(textBefore));
      const span = document.createElement('span');
      span.textContent = '@';
      mirror.appendChild(span);
      document.body.appendChild(mirror);

      const spanRect = span.getBoundingClientRect();
      document.body.removeChild(mirror);

      const top = taRect.top + (spanRect.top - mirror.getBoundingClientRect().top) + lineHeight - ta.scrollTop;
      const left = taRect.left + (spanRect.left - mirror.getBoundingClientRect().left);

      setDropdownPos({
        top: Math.min(top, window.innerHeight - 200),
        left: Math.max(8, Math.min(left, window.innerWidth - 240)),
      });
    } else {
      // Fallback: just below textarea
      setDropdownPos({
        top: taRect.bottom + 4,
        left: taRect.left,
      });
    }
  }, [mentionStart, value]);

  useEffect(() => {
    if (showSuggestions) updateDropdownPosition();
  }, [showSuggestions, updateDropdownPosition]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        textareaRef.current && !textareaRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
        setMentionStart(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on scroll of any parent (dropdown position would be stale)
  useEffect(() => {
    if (!showSuggestions) return;
    function handleScroll() {
      updateDropdownPosition();
    }
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [showSuggestions, updateDropdownPosition]);

  function handleChange(e: ChangeEvent<HTMLTextAreaElement>) {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    onChange(e);

    // Check if we're in a @mention context
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');

    if (atIndex !== -1) {
      const charBefore = atIndex > 0 ? newValue[atIndex - 1] : ' ';
      if (charBefore === ' ' || charBefore === '\n' || charBefore === '\t' || atIndex === 0) {
        const query = textBeforeCursor.substring(atIndex + 1).toLowerCase();
        if (!query.includes(' ') && !query.includes('\n')) {
          const filtered = variables.filter((v) => v.toLowerCase().includes(query));
          if (filtered.length > 0) {
            setSuggestions(filtered);
            setSelectedIndex(0);
            setMentionStart(atIndex);
            setShowSuggestions(true);
            return;
          }
        }
      }
    }

    setShowSuggestions(false);
    setMentionStart(null);
  }

  function insertSuggestion(varName: string) {
    if (mentionStart === null) return;
    const ta = textareaRef.current;
    const cursorPos = ta?.selectionStart ?? value.length;
    const before = value.substring(0, mentionStart);
    const after = value.substring(cursorPos);

    // If this variable has a saved formula, insert the formula text instead
    const insertText = savedFormulas[varName] ?? varName;
    const newValue = before + insertText + ' ' + after;

    const syntheticEvent = {
      target: { value: newValue, selectionStart: mentionStart + insertText.length + 1 },
    } as ChangeEvent<HTMLTextAreaElement>;
    onChange(syntheticEvent);

    setShowSuggestions(false);
    setMentionStart(null);

    setTimeout(() => {
      if (ta) {
        ta.focus();
        const newPos = mentionStart + insertText.length + 1;
        ta.setSelectionRange(newPos, newPos);
      }
    }, 0);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertSuggestion(suggestions[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setMentionStart(null);
    }
  }

  const dropdown = showSuggestions && suggestions.length > 0 ? createPortal(
    <div
      ref={dropdownRef}
      role="listbox"
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        left: dropdownPos.left,
        zIndex: 99999,
        backgroundColor: '#fff',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        maxHeight: '200px',
        overflowY: 'auto',
        minWidth: '220px',
        maxWidth: '340px',
      }}
    >
      {suggestions.map((s, i) => (
        <div
          key={s}
          role="option"
          aria-selected={i === selectedIndex}
          onMouseDown={(e) => { e.preventDefault(); insertSuggestion(s); }}
          onMouseEnter={() => setSelectedIndex(i)}
          style={{
            padding: '0.5rem 0.75rem',
            fontSize: '0.875rem',
            cursor: 'pointer',
            backgroundColor: i === selectedIndex ? '#eff6ff' : '#fff',
            color: '#1f2937',
            borderBottom: i < suggestions.length - 1 ? '1px solid #f3f4f6' : 'none',
            fontWeight: i === selectedIndex ? 600 : 400,
          }}
        >
          <span style={{ color: '#6b7280', marginRight: '0.25rem' }}>@</span>
          {s}
          {savedFormulas[s] && (
            <span style={{
              marginLeft: '0.5rem',
              fontSize: '0.7rem',
              padding: '0.1rem 0.35rem',
              borderRadius: '9999px',
              backgroundColor: '#f0fdf4',
              color: '#166534',
              fontWeight: 500,
            }}>
              formula
            </span>
          )}
        </div>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <div className={`form-field ${error ? 'form-field-error' : ''}`}>
      {label && <label htmlFor={textareaId}>{label}</label>}
      <textarea
        ref={textareaRef}
        id={textareaId}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        rows={rows}
        placeholder={placeholder}
        required={required}
        aria-invalid={!!error}
        aria-describedby={errorId || undefined}
      />
      {dropdown}
      {error && <span id={errorId} className="field-error" role="alert">{error}</span>}
    </div>
  );
}
